import { Component, OnInit, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, EMPTY, switchMap, takeUntil, debounceTime, distinctUntilChanged, take } from 'rxjs';
import { BaseComponent } from '../../base.component';
import { LexbeCase } from '../../../models/case.model';
import { CaseManager } from '../../../managers/case.manager';
import { UserConfigsService } from '../../../services/user-configs.service';

@Component({
    selector: 'lex-cases',
    templateUrl: './cases.component.html',
    styleUrls: ['./cases.component.scss'],
    standalone: false
})
export class CasesComponent extends BaseComponent implements OnInit {

	@ViewChild('leftScroll') leftScroll: CdkVirtualScrollViewport;

	public cases$: Observable<LexbeCase[]>;
	public selectedCase: LexbeCase = null;
	private selectedCaseId: string = '';

	private searchTerm = new BehaviorSubject<string>('');
	public searchText: string = '';

	public selectedOrderBy: [string, "ASC" | "DESC"];
	public orderByValues: Array<{ orderBy: [string, "ASC" | "DESC"], displayName: string }> = [
		{ orderBy: ['Account.AccountName', 'ASC'], displayName: 'Account Name' },
		{ orderBy: ['CaseName', 'ASC'], displayName: 'Case Name' },
		{ orderBy: ['DateTimeCreated', 'DESC'], displayName: 'Created Date' },]

	constructor(
		private caseManager: CaseManager,
		private userConfigsService: UserConfigsService,
		private router: Router,
		private activatedRoute: ActivatedRoute) {
		super();
		this.selectedOrderBy = this.orderByValues[1].orderBy;
		this.cases$ = this.caseManager.caseCollection$;
		this.cases$.pipe(
			switchMap((data) => {
				if (data.length === 0 && this.selectedCase) {
					this.selectCase(null);
				}
				if (data.length === 1) {
					this.selectCase(data[0]);
				}
				else if (this.selectedCaseId) {
					this.selectCase(data.find(x => x.caseId === this.selectedCaseId));
					setTimeout(() => this.leftScroll.scrollToIndex(data.indexOf(this.selectedCase)));
				}
				else if (this.selectedCase?.caseId) {
					this.selectCase(data.find(x => x.caseId === this.selectedCase.caseId));
					setTimeout(() => this.leftScroll.scrollToIndex(data.indexOf(this.selectedCase)));
				}
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
		this.searchTerm.pipe(
			debounceTime(500),
			distinctUntilChanged(),
			switchMap((term: string) => {
				this.caseManager.refreshAdminCases(term, this.selectedOrderBy);				
				if (this.activatedRoute.snapshot.queryParamMap.has('accountId') && this.activatedRoute.snapshot.queryParamMap.get('accountId') !== this.searchText) {
					this.router.navigate([], { relativeTo: this.activatedRoute });
				}
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
		this.activatedRoute.queryParams.pipe(
			switchMap((param) => {
				if (param['caseId']) {
					this.cases$.pipe(take(1)).subscribe(data => {
						if (data.length > 0) {
							if (this.selectedCase?.caseId !== param['caseId'])
								this.selectCase(data.find(x => x.caseId === param['caseId']));
						}
						else
							this.selectedCaseId = param['caseId'];
					});
				}
				else if (param['accountId']) {
					this.updateSearchText(param['accountId']);
				}
				else if (!param['caseId'] && this.selectedCase) {
					this.selectCase(null);
				}
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	ngOnInit(): void {
		if (!this.selectedCaseId && !this.selectedCase && !this.searchText)
			this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: { caseId: this.userConfigsService.currentConfig.currentCase.caseId } });
	}

	public selectCase(lexbeCase?: LexbeCase): void {
		if (lexbeCase) {
			this.selectedCase = lexbeCase;
			this.selectedCaseId = '';
			this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: { caseId: this.selectedCase.caseId } });
		}
		else {
			this.selectedCase = null;
			this.router.navigate([], { relativeTo: this.activatedRoute });
		}
	}

	public updateSearchText(term: string): void {
		this.searchText = term;
		this.searchTerm.next(term);
	}

	public updateOrderBy(): void {
		if (this.selectedCase)
			this.selectCase(null);
		this.caseManager.refreshAdminCases(this.searchText, this.selectedOrderBy);
	}
}
